import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { classNames } from '../utils/classNames';

interface SystemEvent {
  id: number;
  ts: string;
  level: 'info' | 'warning' | 'error';
  source?: string;
  type: string;
  message?: string;
  details?: string;
  shop_id?: number;
  user_id?: number;
  ip?: string;
  request_path?: string;
}

const levelTone: Record<SystemEvent['level'], { bg: string; text: string }> = {
  error: { bg: 'bg-danger/10', text: 'text-danger' },
  warning: { bg: 'bg-warning/10', text: 'text-warning' },
  info: { bg: 'bg-primary-50', text: 'text-primary-600' },
};

const Monitoring: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<SystemEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  const offset = (page - 1) * limit;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);

  useEffect(() => {
    if (user?.role === 'owner') {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, level, type, page]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit, offset };
      if (level) params.level = level;
      if (type) params.type = type;
      const { data } = await api.get('/owner/system/events', { params });
      setEvents(data.events || []);
      setTotal(data.total || 0);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleForwardTelegram = async (eventId: number) => {
    try {
      await api.post(`/owner/system/events/${eventId}/forward-telegram`);
      alert('Отправлено в Telegram');
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Ошибка отправки');
    }
  };

  const [alertEnabled, setAlertEnabled] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMinLevel, setAutoMinLevel] = useState<'info' | 'warning' | 'error'>('error');
  const [autoTypes, setAutoTypes] = useState('');

  useEffect(() => {
    if (user?.role === 'owner') {
      (async () => {
        try {
          const { data } = await api.get('/owner/system/alerts');
          setAlertEnabled(Boolean(data?.telegram_enabled));
          setBotToken(data?.telegram_bot_token || '');
          setChatId(data?.telegram_chat_id || '');
          setAutoEnabled(Boolean(data?.autosend_enabled));
          setAutoMinLevel(data?.autosend_min_level || 'error');
          setAutoTypes(data?.autosend_types || '');
        } catch {}
      })();
    }
  }, [user]);

  const saveAlerts = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/owner/system/alerts', {
        telegram_enabled: alertEnabled,
        telegram_bot_token: botToken,
        telegram_chat_id: chatId,
        autosend_enabled: autoEnabled,
        autosend_min_level: autoMinLevel,
        autosend_types: autoTypes,
      });
      alert('Сохранено');
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Ошибка сохранения');
    }
  };

  if (user?.role !== 'owner') {
    return <div className="flex min-h-[40vh] items-center justify-center text-danger">Доступ запрещен</div>;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold text-[var(--app-heading)]">Мониторинг системы</h2>
        <p className="text-sm text-[var(--app-text-muted)]">Ошибки и неудачные запросы, с пересылкой в Telegram.</p>
      </header>

      <section className="card">
        <div className="card-body space-y-4">
          <h3 className="text-lg font-semibold text-[var(--app-heading)]">Фильтры</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="space-y-2 text-sm font-medium text-[var(--app-text)]">
              <span>Уровень</span>
              <select
                className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                value={level}
                onChange={(e) => {
                  setPage(1);
                  setLevel(e.target.value);
                }}
              >
                <option value="">Все</option>
                <option value="info">info</option>
                <option value="warning">warning</option>
                <option value="error">error</option>
              </select>
            </label>
            <label className="space-y-2 text-sm font-medium text-[var(--app-text)] lg:col-span-2">
              <span>Тип</span>
              <input
                className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] placeholder:text-[var(--app-text-muted)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                value={type}
                onChange={(e) => {
                  setPage(1);
                  setType(e.target.value);
                }}
                placeholder="например auth.login_failed"
              />
            </label>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-[var(--app-heading)]">События</h3>
          <div className="text-sm text-[var(--app-text-muted)]">Всего: {total}</div>
        </div>
        {loading ? (
          <div className="card-body text-sm text-[var(--app-text-muted)]">Загрузка…</div>
        ) : (
          <div className="divide-y divide-[var(--app-border)]">
            {events.map((ev) => (
              <div key={ev.id} className="px-6 py-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--app-text-muted)]">
                      <span
                        className={classNames(
                          'inline-flex items-center rounded-full px-2.5 py-1 font-semibold capitalize',
                          levelTone[ev.level].bg,
                          levelTone[ev.level].text
                        )}
                      >
                        {ev.level}
                      </span>
                      <span>{new Date(ev.ts).toLocaleString('ru-RU')}</span>
                      <span>• {ev.source || 'system'}</span>
                      <span>• {ev.type}</span>
                    </div>
                    {ev.message ? (
                      <div className="text-sm font-semibold text-[var(--app-heading)]">{ev.message}</div>
                    ) : null}
                    {ev.details ? (
                      <pre className="max-h-48 overflow-auto rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-3 py-2 text-xs text-[var(--app-text)]">
                        {ev.details}
                      </pre>
                    ) : null}
                    <div className="text-xs text-[var(--app-text-muted)]">
                      {ev.request_path || ''}
                      {ev.ip ? ` • IP: ${ev.ip}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => handleForwardTelegram(ev.id)}
                    className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600 transition hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                  >
                    В Telegram
                  </button>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="px-6 py-6 text-center text-sm text-[var(--app-text-muted)]">Нет событий</div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--app-border)] px-6 py-4 text-sm text-[var(--app-text-muted)]">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={classNames(
              'inline-flex items-center rounded-full px-3 py-1 font-semibold transition',
              page <= 1
                ? 'cursor-not-allowed bg-[var(--app-surface-subtle)] text-[var(--app-text-muted)]/60'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            )}
          >
            Назад
          </button>
          <div>
            Стр. {page} из {totalPages}
          </div>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={classNames(
              'inline-flex items-center rounded-full px-3 py-1 font-semibold transition',
              page >= totalPages
                ? 'cursor-not-allowed bg-[var(--app-surface-subtle)] text-[var(--app-text-muted)]/60'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            )}
          >
            Вперёд
          </button>
        </div>
      </section>

      <section className="card">
        <div className="card-body space-y-4">
          <h3 className="text-lg font-semibold text-[var(--app-heading)]">Настройки оповещений</h3>
          <form onSubmit={saveAlerts} className="space-y-4">
            <label className="inline-flex items-center gap-3 text-sm text-[var(--app-text)]">
              <input
                id="alertEnabled"
                type="checkbox"
                checked={alertEnabled}
                onChange={(e) => setAlertEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-[var(--app-border)] text-primary-500 focus:ring-primary-400"
              />
              <span>Включить Telegram оповещения</span>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1 text-sm text-[var(--app-text)]">
                <span className="font-medium">Bot Token</span>
                <input
                  className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="12345:ABC..."
                />
              </label>
              <label className="space-y-1 text-sm text-[var(--app-text)]">
                <span className="font-medium">Chat ID</span>
                <input
                  className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="@channel или numeric id"
                />
              </label>
            </div>

            <div className="space-y-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-4 py-4">
              <label className="inline-flex items-center gap-3 text-sm text-[var(--app-text)]">
                <input
                  id="autoEnabled"
                  type="checkbox"
                  checked={autoEnabled}
                  onChange={(e) => setAutoEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-[var(--app-border)] text-primary-500 focus:ring-primary-400"
                />
                <span>Автопересылка событий в Telegram</span>
              </label>
              <div className="grid gap-3 lg:grid-cols-3">
                <label className="space-y-1 text-sm text-[var(--app-text)]">
                  <span className="font-medium">Минимальный уровень</span>
                  <select
                    className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                    value={autoMinLevel}
                    onChange={(e) => setAutoMinLevel(e.target.value as any)}
                  >
                    <option value="info">info</option>
                    <option value="warning">warning</option>
                    <option value="error">error</option>
                  </select>
                </label>
                <label className="lg:col-span-2 space-y-1 text-sm text-[var(--app-text)]">
                  <span className="font-medium">Типы событий (через запятую)</span>
                  <input
                    className="w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                    value={autoTypes}
                    onChange={(e) => setAutoTypes(e.target.value)}
                    placeholder="например auth.login_failed, provider.webhook_invalid"
                  />
                  <p className="text-xs text-[var(--app-text-muted)]">Если пусто — все типы, соответствующие уровню.</p>
                </label>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
              >
                Сохранить
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Monitoring;


